import { isOk } from "@/common/common";
import { parseDateInput, toLocalDateISOString, today } from "@/utils/Dates";
import { DCEvent, EventDispatcher, WithStoreEvent } from "@/events/eventsDomain";
import { TMessage } from "@/i18n/tt";
import { customFieldsApiClient } from "./customFieldsApiClient";
import {
  CustomFieldDataType,
  CustomFieldRequirement,
  systemFieldLabels,
} from "./customFieldsDomain";
import {
  blankUnmappedCells,
  cellEditor,
  ImportFieldOption,
  resultRows,
  SpreadsheetImportGrid,
  SpreadsheetImportIssue,
  SpreadsheetImportResult,
  SpreadsheetImportRow,
} from "./spreadsheetImportDomain";
import {
  changeAndValidate,
  openSpreadsheetImport,
  prepSpreadsheetImportFlow,
  setupOf,
  SpreadsheetImportFlow,
  SpreadsheetImportFlowProps,
  SpreadsheetImportState,
} from "./spreadsheetImportFlow";

export type CompanyDataField = "COMPANY_NAME" | "TIN_NUMBER" | "DATE_OF_INCORPORATION";

export type AddressPart =
  | "ADDRESS_LINE1"
  | "ADDRESS_LINE2"
  | "POSTAL_CODE"
  | "POSTAL_PLACE"
  | "COUNTRY";

export type EntityDataTarget =
  | { targetType: "SYSTEM"; field: CompanyDataField }
  | { targetType: "ADDRESS"; addressType: "BUSINESS" | "POSTAL"; part: AddressPart }
  | { targetType: "CUSTOM"; definitionId: string };

export type EntityDataImportField = {
  target: EntityDataTarget;
  dataType: CustomFieldDataType;
  name?: string;
  options?: string[];
  effectiveDate?: CustomFieldRequirement;
};

export type EntityDataImportSheet = {
  headers: string[];
  rows: SpreadsheetImportRow[];
  fields: EntityDataImportField[];
  countryCodes: string[];
  fieldCountryCodes: string[];
};

export type EntityDataImportRequest = {
  rows: SpreadsheetImportRow[];
  columns: (EntityDataTarget | null)[];
  effectiveFrom?: string;
};

export type EntityDataImportResponse = {
  updated: number;
  unchanged: number;
  issues: SpreadsheetImportIssue[];
};

type EntityDataImportSetup = Omit<EntityDataImportSheet, "rows">;

type EntityDataImportExtra = { effectiveDate: string };

export type EntityDataImportState = SpreadsheetImportState<
  EntityDataImportSetup,
  EntityDataImportResponse,
  EntityDataImportExtra
>;

const addressLabels: Record<"BUSINESS" | "POSTAL", TMessage> = {
  BUSINESS: systemFieldLabels.BUSINESS_ADDRESS,
  POSTAL: systemFieldLabels.POSTAL_ADDRESS,
};

const addressPartLabels: Record<AddressPart, TMessage> = {
  ADDRESS_LINE1: { key: "entity-data.address-part-line1" },
  ADDRESS_LINE2: { key: "entity-data.address-part-line2" },
  POSTAL_CODE: { key: "entity-data.address-part-postal-code" },
  POSTAL_PLACE: { key: "entity-data.address-part-postal-place" },
  COUNTRY: { key: "entity-data.address-part-country" },
};

export const fieldKey = (target: EntityDataTarget): string => {
  switch (target.targetType) {
    case "SYSTEM":
      return `SYSTEM:${target.field}`;
    case "ADDRESS":
      return `ADDRESS:${target.addressType}:${target.part}`;
    case "CUSTOM":
      return `CUSTOM:${target.definitionId}`;
  }
};

export const fieldLabel = (field: EntityDataImportField): TMessage => {
  const target = field.target;
  switch (target.targetType) {
    case "SYSTEM":
      return systemFieldLabels[target.field];
    case "ADDRESS":
      return {
        key: "entity-data.spreadsheet-address-field",
        params: {
          address: addressLabels[target.addressType],
          part: addressPartLabels[target.part],
        },
      };
    case "CUSTOM":
      return { translated: field.name ?? "" };
  }
};

export const fieldSection = (field: EntityDataImportField): TMessage =>
  field.target.targetType === "CUSTOM"
    ? { key: "entity-data.spreadsheet-custom-fields" }
    : { key: "entity-data.spreadsheet-system-fields" };

const entityDataImportFlow: SpreadsheetImportFlow<
  EntityDataImportSetup,
  EntityDataImportRequest,
  EntityDataImportResponse,
  EntityDataImportExtra
> = {
  set: (change) => ({ type: "update-store", data: { entityDataImport: change } }),
  stateOf: (root) => root.entityDataImport,
  initial: { effectiveDate: "" },
  requestOf: (state) => importRequest(state),
  validate: customFieldsApiClient.validateEntityDataImport,
  import: customFieldsApiClient.importEntityData,
};

export const openEntityDataImport = (
  accountId: string,
  files: File[] | null,
  afterImport: DCEvent[],
): DCEvent[] =>
  openSpreadsheetImport(
    entityDataImportFlow,
    accountId,
    files,
    afterImport,
    customFieldsApiClient.readEntityDataImport,
    ({ rows, ...setup }) => ({ setup, rows, columns: setup.headers.map(() => undefined) }),
  );

const noSetup: EntityDataImportSetup = {
  headers: [],
  fields: [],
  countryCodes: [],
  fieldCountryCodes: [],
};

const fieldsByKeyOf = (state: EntityDataImportState): Map<string, EntityDataImportField> =>
  new Map(setupOf(state, noSetup).fields.map((field) => [fieldKey(field.target), field]));

const effectiveDateIssue = (state: EntityDataImportState): TMessage | undefined => {
  const { parsed, error } = parseDateInput(state.effectiveDate);
  if (error) return { key: "date-picker.invalid-date-message" };
  if (parsed && parsed > today()) return { key: "entity-data.spreadsheet-date-after-today" };
  return undefined;
};

const effectiveFromOf = (state: EntityDataImportState): string | undefined =>
  state.effectiveDate && !effectiveDateIssue(state) ? state.effectiveDate : undefined;

export const importRequest = (state: EntityDataImportState): EntityDataImportRequest => {
  const fieldsByKey = fieldsByKeyOf(state);
  const columns = state.columns.map((key) => (key && fieldsByKey.get(key)?.target) || null);
  return {
    rows: blankUnmappedCells(state.rows, (index) => columns[index] !== null),
    columns,
    effectiveFrom: effectiveFromOf(state),
  };
};

const changeEffectiveDate = (effectiveDate: string): WithStoreEvent =>
  changeAndValidate(entityDataImportFlow, (state) =>
    state.effectiveDate === effectiveDate ? state : { ...state, effectiveDate },
  );

const effectiveDateTag = (
  field: EntityDataImportField,
  effectiveFrom: string | undefined,
): TMessage | undefined => {
  if (!field.effectiveDate) return undefined;
  if (effectiveFrom) {
    return {
      key: "entity-data.spreadsheet-effective-from",
      params: { date: { type: "date", value: effectiveFrom } },
    };
  }
  return field.effectiveDate === "REQUIRED"
    ? undefined
    : { key: "entity-data.spreadsheet-effective-from-today" };
};

const fieldOption = (
  field: EntityDataImportField,
  effectiveFrom: string | undefined,
): ImportFieldOption => ({
  key: fieldKey(field.target),
  label: fieldLabel(field),
  section: fieldSection(field),
  editor: cellEditor(field.dataType, field.options),
  tag: effectiveDateTag(field, effectiveFrom),
});

export const gridOf = (state: EntityDataImportState): SpreadsheetImportGrid => ({
  headers: setupOf(state, noSetup).headers,
  rows: state.rows,
  columns: state.columns,
  fieldsByKey: new Map(
    setupOf(state, noSetup).fields.map((field) => [
      fieldKey(field.target),
      fieldOption(field, effectiveFromOf(state)),
    ]),
  ),
  errorsOnly: state.errorsOnly,
  selectedOnly: state.selectedOnly,
});

const importResultOf = (
  state: EntityDataImportState,
  response: EntityDataImportResponse,
): SpreadsheetImportResult => ({
  counts: [
    { key: "updated", label: { key: "entity-data.import-updated" }, value: response.updated },
    {
      key: "unchanged",
      label: { key: "entity-data.import-unchanged" },
      value: response.unchanged,
    },
    {
      key: "rejected",
      label: { key: "entity-data.spreadsheet-not-imported" },
      value: response.issues.length,
    },
  ],
  nothingImported: response.updated === 0,
  rows: resultRows(gridOf(state), response.issues),
});

export type EntityDataImportViewProps = SpreadsheetImportFlowProps<EntityDataImportSetup> & {
  effectiveDate: string;
  effectiveDateIssue?: TMessage;
  maxEffectiveDate: string;
  canImport: boolean;
  result?: SpreadsheetImportResult;
  onChangeEffectiveDate: (input: string) => void;
};

export const prepEntityDataImportView = (
  state: EntityDataImportState | undefined,
  dispatch: EventDispatcher,
): EntityDataImportViewProps | undefined => {
  if (!state) {
    return undefined;
  }
  const dateIssue = effectiveDateIssue(state);
  return {
    ...prepSpreadsheetImportFlow(
      entityDataImportFlow,
      state,
      setupOf(state, noSetup),
      gridOf(state),
      isOk(state.validation) ? state.validation.data.issues : [],
      dispatch,
    ),
    effectiveDate: state.effectiveDate,
    effectiveDateIssue: dateIssue,
    maxEffectiveDate: toLocalDateISOString(today()),
    canImport:
      state.columns.some((key) => key !== undefined) && !dateIssue && isOk(state.setupResult),
    result: isOk(state.importResult) ? importResultOf(state, state.importResult.data) : undefined,
    onChangeEffectiveDate: (input) => dispatch([changeEffectiveDate(input)]),
  };
};
